import React from 'react'
import { BrandPlanIntegrations, SelectedBlogPlatform, SelectedEmailPlatform } from 'types'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import {
  getTacticInfo,
  tacticHasGenericIntegration,
  TacticInfo,
  isBrandPlanIntegrationType,
  isSocialMediaIntegration,
} from '../utils'
import { useIntegrationHeartbeatStatus } from 'hooks/useIntegrationHeartbeatStatus'
import { TacticIntegrationListVariant } from '../TacticIntegrationList'
import { DisconnectedIntegration } from './DisconnectedIntegration'
import { ConnectedIntegration } from './ConnectedIntegration'
import { UnconnectedIntegration } from './UnconnectedIntegration'
import { GenericIntegration } from './GenericIntegration'
import { MultiAccountIntegration } from './MultiAccountIntegration'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCampaignsEnabled } from 'hooks/queries/brandPlans/useCampaignsEnabled'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

interface TacticIntegrationProps {
  tactic?: BrandPlanIntegrations
  tacticInfo?: TacticInfo
  index: number
  selectedBlogPlatform?: SelectedBlogPlatform
  selectedEmailPlatform?: SelectedEmailPlatform
  isHighlighted?: boolean
  onConnect?: (integrations: ExternalIntegration[]) => void
  variant?: TacticIntegrationListVariant
}

export const TacticIntegration: React.FC<TacticIntegrationProps> = ({
  tactic,
  tacticInfo: tacticInfoProp,
  index,
  selectedBlogPlatform,
  selectedEmailPlatform,
  isHighlighted,
  onConnect,
  variant,
}) => {
  const { data } = useExternalIntegrations()
  const { isIntegrationDisconnected } = useIntegrationHeartbeatStatus()
  const multipleAccountsEnabled = useFlipperFlag('multiple_accounts')
  const isCampaignsEnabled = useCampaignsEnabled()
  const workspace = useCurrentWorkspace()

  // Use provided tacticInfo or get it from tactic
  const tacticInfo =
    tacticInfoProp || (tactic ? getTacticInfo(tactic, selectedBlogPlatform, selectedEmailPlatform) : null)

  if (!tacticInfo && tacticHasGenericIntegration(tactic)) {
    return <GenericIntegration index={index} tactic={tactic as BrandPlanIntegrations} />
  }

  if (!tacticInfo) {
    return null
  }

  const { icon: IconComponent, name: displayName, integrationType } = tacticInfo

  const integration = data?.external_integrations?.find((integration) => integration.provider === integrationType)

  const shouldShowMultiAccount =
    multipleAccountsEnabled &&
    !isCampaignsEnabled &&
    variant === 'integrations_page' &&
    integration &&
    isBrandPlanIntegrationType(integration.provider) &&
    isSocialMediaIntegration(integration.provider) &&
    !workspace?.is_ap_freemium

  if (shouldShowMultiAccount) {
    const allIntegrationsOfProvider = data?.external_integrations?.filter((i) => i.provider === integrationType) || []

    if (allIntegrationsOfProvider.length > 0) {
      return (
        <MultiAccountIntegration
          IconComponent={IconComponent}
          integrations={allIntegrationsOfProvider}
          index={index}
          variant={variant}
          tactic={tactic}
        />
      )
    }
  }

  // Fall back to individual integration components if not showing multi-account UI
  if (integration && isIntegrationDisconnected(integration)) {
    return <DisconnectedIntegration integration={integration} index={index} variant={variant} />
  }

  if (integration) {
    return <ConnectedIntegration integration={integration} index={index} variant={variant} tactic={tactic} />
  }

  return (
    <UnconnectedIntegration
      displayName={displayName}
      IconComponent={IconComponent}
      index={index}
      integration={integration}
      integrationType={integrationType}
      isHighlighted={isHighlighted}
      onConnect={onConnect}
      tactic={tactic}
    />
  )
}
